import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useEventDetail, useUpdateEvent, Event } from "@/hooks/useEvents";
import { useRoutes } from "@/hooks/useRoutes";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowLeft, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import PhotoUpload from "@/components/PhotoUpload";
import { useEventPhotoUpload } from "@/hooks/useEventPhotoUpload";
import { toast } from "sonner";
import { notifyEventParticipants } from "@/hooks/useEventNotifications";
import { supabase } from "@/integrations/supabase/client";

const EditEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: event, isLoading: eventLoading } = useEventDetail(eventId);
  const { routes } = useRoutes();
  const updateEventMutation = useUpdateEvent();
  const { uploadPhotos, deletePhoto, isUploading } = useEventPhotoUpload();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'ride' as Event['event_type'],
    route_id: '',
    start_date: '',
    estimated_duration_hours: '',
    meeting_point_description: '',
    max_participants: '',
    equipment_requirements: [] as string[],
    notes: '',
    waiver_required: false,
    waiver_text: '',
  });

  const defaultWaiverTemplate = `ASSUMPTION OF RISK, WAIVER OF LIABILITY, AND INDEMNITY AGREEMENT

By participating in this off-road event, I acknowledge and accept the following:

1. NATURE OF ACTIVITY
I understand that off-road vehicle operation involves inherent risks including, but not limited to: vehicle rollovers, collisions, mechanical failures, encounters with wildlife, extreme weather conditions, remote locations with limited communication and emergency services, and the possibility of serious injury or death.

2. ASSUMPTION OF RISK
I voluntarily choose to participate in this event with full knowledge of the dangers involved. I accept and assume all risks of injury, death, or property damage, whether caused by the negligence of the organizers, other participants, or otherwise.

3. WAIVER OF LIABILITY
I hereby release, waive, discharge, and covenant not to sue Airing Down (the platform provider), the event organizers, landowners, sponsors, volunteers, and all affiliated parties from any and all liability for any loss, damage, injury, or death to my person or property arising from my participation in this event.

4. RELEASE OF PLATFORM PROVIDER
In addition to releasing the event organizers, I specifically release and hold harmless Airing Down, its owners, operators, employees, contractors, and affiliates from any liability arising from:
- My use of the Airing Down platform to discover, register for, or communicate about this event
- Any technical issues, errors, or omissions in event information displayed on the platform
- Any miscommunication or misunderstanding arising from platform features
- The actions or conduct of other platform users
I understand that Airing Down is merely a technology platform that facilitates connections between event organizers and participants, and does not organize, oversee, or assume responsibility for any events listed on the platform.

5. INSURANCE AND MEDICAL EXPENSES
I confirm that I carry adequate health and vehicle insurance coverage. I agree to be financially responsible for any medical expenses, rescue operations, or property damage resulting from my participation.

6. RULES AND REGULATIONS
I agree to:
- Operate my vehicle safely and responsibly at all times
- Follow all instructions from event organizers and land managers
- Stay on designated trails and respect private property
- Carry required safety equipment
- Possess valid driver's license, vehicle registration, and insurance
- Not participate under the influence of alcohol or drugs

7. INDEMNIFICATION
I agree to indemnify and hold harmless Airing Down, the event organizers, and all affiliated parties from any claims, actions, or losses arising from my participation in this event or use of the Airing Down platform.

8. MEDIA RELEASE
I grant permission for event organizers to use photographs or video footage taken during the event for promotional purposes.

I HAVE READ THIS AGREEMENT CAREFULLY AND UNDERSTAND ITS CONTENTS. I AM AWARE THAT THIS IS A RELEASE OF LIABILITY AND A CONTRACT AND I SIGN IT OF MY OWN FREE WILL.`;

  const handleUseTemplate = () => {
    setFormData({ ...formData, waiver_text: defaultWaiverTemplate });
    toast.success('Default waiver template loaded');
  };

  const [equipmentInput, setEquipmentInput] = useState('');
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [newPhotos, setNewPhotos] = useState<Array<{ file: File; preview: string; id: string }>>([]);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title,
        description: event.description || '',
        event_type: event.event_type,
        route_id: event.route_id || '',
        start_date: event.start_date ? new Date(event.start_date).toISOString().slice(0, 16) : '',
        estimated_duration_hours: event.estimated_duration_hours?.toString() || '',
        meeting_point_description: event.meeting_point_description || '',
        max_participants: event.max_participants?.toString() || '',
        equipment_requirements: event.equipment_requirements || [],
        notes: event.notes || '',
        waiver_required: event.waiver_required || false,
        waiver_text: event.waiver_text || '',
      });
      setExistingPhotos(event.photo_urls || []);
    }
  }, [event]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <p className="text-center text-muted-foreground">Please sign in to edit events</p>
        </main>
      </div>
    );
  }

  if (eventLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8 max-w-2xl">
          <Skeleton className="h-96 w-full" />
        </main>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <p className="text-center text-muted-foreground">Event not found</p>
        </main>
      </div>
    );
  }

  if (event.created_by !== user.id) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <p className="text-center text-muted-foreground">You don't have permission to edit this event</p>
        </main>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!eventId || !user) return;

    // Upload new photos
    let newPhotoUrls: string[] = [];
    if (newPhotos.length > 0) {
      toast.info('Uploading new photos...');
      newPhotoUrls = await uploadPhotos(newPhotos.map(p => p.file), user.id);
    }

    // Combine existing and new photos
    const allPhotos = [...existingPhotos, ...newPhotoUrls];

    const updates = {
      title: formData.title,
      description: formData.description || undefined,
      event_type: formData.event_type,
      route_id: formData.route_id || undefined,
      start_date: new Date(formData.start_date).toISOString(),
      estimated_duration_hours: formData.estimated_duration_hours ? Number(formData.estimated_duration_hours) : undefined,
      meeting_point_description: formData.meeting_point_description || undefined,
      max_participants: formData.max_participants ? Number(formData.max_participants) : undefined,
      equipment_requirements: formData.equipment_requirements.length > 0 ? formData.equipment_requirements : undefined,
      notes: formData.notes || undefined,
      photo_urls: allPhotos.length > 0 ? allPhotos : undefined,
      waiver_required: formData.waiver_required,
      waiver_text: formData.waiver_required && formData.waiver_text ? formData.waiver_text : undefined,
    };

    updateEventMutation.mutate({ eventId, updates }, {
      onSuccess: async () => {
        // Notify participants about the update
        await notifyEventParticipants(
          eventId,
          'event_updated',
          'Event Updated',
          `The event "${formData.title}" has been updated. Please check the latest details.`
        );
        navigate(`/events/${eventId}`);
      },
    });
  };

  const handleRemoveExistingPhoto = async (photoUrl: string) => {
    const success = await deletePhoto(photoUrl);
    if (success) {
      setExistingPhotos(existingPhotos.filter(url => url !== photoUrl));
      toast.success('Photo removed');
    }
  };

  const addEquipment = () => {
    if (equipmentInput.trim() && !formData.equipment_requirements.includes(equipmentInput.trim())) {
      setFormData({
        ...formData,
        equipment_requirements: [...formData.equipment_requirements, equipmentInput.trim()]
      });
      setEquipmentInput('');
    }
  };

  const removeEquipment = (item: string) => {
    setFormData({
      ...formData,
      equipment_requirements: formData.equipment_requirements.filter(eq => eq !== item)
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Button variant="ghost" onClick={() => navigate(`/events/${eventId}`)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Event
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Edit Event</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Saturday Morning Trail Run"
                />
              </div>

              <div>
                <Label htmlFor="event_type">Event Type *</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(value: Event['event_type']) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ride">Ride</SelectItem>
                    <SelectItem value="garage_day">Garage Day</SelectItem>
                    <SelectItem value="cleanup">Cleanup</SelectItem>
                    <SelectItem value="meetup">Meetup</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="overlanding_trip">Overlanding Trip</SelectItem>
                    <SelectItem value="competition">Competition</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="route">Route (Optional)</Label>
                <Select 
                  value={formData.route_id || undefined} 
                  onValueChange={(value) => setFormData({ ...formData, route_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a route (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {routes.map((route) => (
                      <SelectItem key={route.id} value={route.id}>
                        {route.name} {route.length_miles && `(${route.length_miles.toFixed(1)} mi)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start_date">Start Date & Time *</Label>
                <Input
                  id="start_date"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="meeting_point">Meeting Point</Label>
                <Input
                  id="meeting_point"
                  value={formData.meeting_point_description}
                  onChange={(e) => setFormData({ ...formData, meeting_point_description: e.target.value })}
                  placeholder="Trailhead parking lot on Main St"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration (hours)</Label>
                  <Input
                    id="duration"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimated_duration_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_duration_hours: e.target.value })}
                    placeholder="3"
                  />
                </div>
                <div>
                  <Label htmlFor="max_participants">Max Participants</Label>
                  <Input
                    id="max_participants"
                    type="number"
                    min="1"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="equipment">Equipment Requirements</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    id="equipment"
                    value={equipmentInput}
                    onChange={(e) => setEquipmentInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEquipment())}
                    placeholder="e.g., Helmet, Water, Spare tire"
                  />
                  <Button type="button" onClick={addEquipment} variant="secondary">Add</Button>
                </div>
                {formData.equipment_requirements.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.equipment_requirements.map((item) => (
                      <div key={item} className="flex items-center gap-1 bg-secondary text-secondary-foreground px-3 py-1 rounded-md text-sm">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeEquipment(item)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your event..."
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="What to bring, special requirements, etc."
                  rows={3}
                />
              </div>

              {/* Existing Photos */}
              {existingPhotos.length > 0 && (
                <div>
                  <Label>Current Photos</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
                    {existingPhotos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                          <img
                            src={photo}
                            alt={`Event photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemoveExistingPhoto(photo)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Photos Upload */}
              <PhotoUpload
                photos={newPhotos}
                onPhotosChange={setNewPhotos}
                maxPhotos={10 - existingPhotos.length}
              />

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="waiver-required" className="text-base">
                      Require Waiver
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Participants must accept a waiver before RSVPing
                    </p>
                  </div>
                  <Switch
                    id="waiver-required"
                    checked={formData.waiver_required}
                    onCheckedChange={(checked) => setFormData({ ...formData, waiver_required: checked })}
                  />
                </div>

                {formData.waiver_required && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="waiver-text">Waiver Text *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleUseTemplate}
                      >
                        Use Default Template
                      </Button>
                    </div>
                    <Textarea
                      id="waiver-text"
                      value={formData.waiver_text}
                      onChange={(e) => setFormData({ ...formData, waiver_text: e.target.value })}
                      placeholder="Enter the waiver terms and conditions or click 'Use Default Template' to load a comprehensive liability waiver..."
                      rows={8}
                      required={formData.waiver_required}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This waiver will be shown to participants when they RSVP
                    </p>
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={updateEventMutation.isPending || isUploading}
              >
                {isUploading ? 'Uploading photos...' : updateEventMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EditEvent;
